// ProcessSteps.tsx
import React, { useState, useEffect, useCallback, useMemo, Suspense, useRef, useReducer } from "react";
import { Card, Row, Col, Alert, Modal, Skeleton, Button } from "antd";
import { API_BASE_URL } from "../constants/config";
import axios, { AxiosError } from "axios";
import type { ProcessStepsProps, RequestDetails } from "./types";
import { WithdrawDetails } from "./WithdrawDetails";
import { ValidationContent } from "./ValidationContent";
import { OperationSummary } from "./OperationSummary";
import ReceiptCard from "./ReceiptCard";
import { useRequestDetailsStore } from '../shared/stores/useRequestDetailsStore';
import { getAndSanitize } from '../shared/utils/sanitization';

type HistoryRecord = any; // Replace 'any' with the correct type
interface ContentLoadingState {
  validation: boolean;
  receipt: boolean;
  details: boolean;
  summary: boolean;
}

interface ImageModalState {
  visible: boolean;
  image: string | null;
}

export const ProcessSteps: React.FC<ProcessStepsProps> = React.memo(({ record }) => {
  const [details, setDetails] = useState<RequestDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [dataAttemptedLoadForId, setDataAttemptedLoadForId] = useState<string | null>(null);
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  // Add counter to force child component re-renders after data updates
  const [updateCounter, setUpdateCounter] = useState<number>(0);

  const [contentLoading, setContentLoading] = useState<ContentLoadingState>({
    validation: false,
    receipt: true,
    details: true,
    summary: true
  });
  const [imageModal, setImageModal] = useState<ImageModalState>({
    visible: false,
    image: null
  });
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [modalImageLoading, setModalImageLoading] = useState(false);

  // Add a ref to track the polling interval
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Add a ref to track the current request's status
  const currentStatusRef = useRef<string | null>(record.status);
  
  // Determine if a status is "active" (processing) or "terminal" (completed)
  const isActiveStatus = useCallback((status: string): boolean => {
    // Only PROCESSING is truly active - REQUIRES_VALIDATION needs manual action
    return ['PROCESSING'].includes(status);
  }, []);

  const isTerminalStatus = useCallback((status: string): boolean => {
    // Terminal statuses including those that require manual action
    return ['COMPLETED', 'REJECTED', 'CANCELED', 'REQUIRES_VALIDATION', 'APPROVED', 'WAITING_FOR_CLIENT_VALIDATION'].includes(status);
  }, []);

// Modified fetchDetails with better state handling and component refreshing
const fetchDetails = useCallback(async (options?: { forceRefresh?: boolean }): Promise<void> => {
  if (isFetching && !options?.forceRefresh) {
    return;
  }

  try {
    setIsFetching(true);

    // Only set loading true on initial fetch, not refreshes
    if (!details || details.requestId !== record.id) {
      setLoading(true);
    }

    const token = getAndSanitize("token");
    if (!token) {
      setError("Se requiere autenticación. Por favor inicie sesión nuevamente.");
      setLoading(false);
      setIsFetching(false);
      return;
    }

    if (!record.id) {
      setError("ID de solicitud no proporcionado.");
      setLoading(false);
      setIsFetching(false);
      return;
    }

    const adminId = record.adminId ||
                  record.user?.adminId ||
                  getAndSanitize("adminId") ||
                  getAndSanitize("userId");

    if (!adminId) {
      setError("Se requiere ID de administrador para ver los detalles.");
      setLoading(false);
      setIsFetching(false);
      return;
    }


    const detailsData = await useRequestDetailsStore.getState().fetchDetails(
      record.id,
      adminId,
      { forceRefresh: options?.forceRefresh || false } // Only force refresh if explicitly requested
    );
    
    if (!detailsData) {
      throw new Error('Error al obtener detalles de la solicitud');
    }
    
    let historyData = record.user?.historyData || [];
    let currentBalance = record.user?.currentBalance || 0;

    if (!record.user?.historyData) {
      try {
        const userResponse = await axios.get<{ 
          success: boolean;
          data: Array<{
            username: string;
            historyData: HistoryRecord[];
            currentBalance: number;
            id: string;
          }>
        }>(
          `${API_BASE_URL}/api/panel/users`,
          { 
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            params: {
              adminId
            }
          }
        );

        const userData = userResponse.data.data.find(
          user => user.username === record.user?.username
        );

        if (userData) {
          historyData = userData.historyData;
          currentBalance = userData.currentBalance;
        }
      } catch (error) {
        console.error('[ProcessSteps] Error fetching user history:', error);
      }
    }

    const effectiveReceipt = 
      detailsData.receipt || 
      record.receipt || 
      detailsData.operation?.receipt || 
      null;

    // Crear objeto completamente nuevo con referencias nuevas para todo
    const transformedData: RequestDetails = JSON.parse(JSON.stringify({
      ...detailsData,
      requestId: record.id,
      status: detailsData.status || record.status,
      validationDetails: detailsData.validationDetails ?? null,
      validationScore: detailsData.validationScore ?? null,
      receipt: effectiveReceipt,
      user: {
        ...record.user,
        ...detailsData.user,
        historyData,
        currentBalance
      },
      requestData: {
        ...detailsData.requestData,
        retryHistory: detailsData.requestData?.retryHistory ?? [],
        processingSteps: (detailsData.requestData?.processingSteps || []).map((step) => ({
          ...step,
          timestamp: typeof step.timestamp === 'string' ? step.timestamp : new Date(step.timestamp).toISOString(),
          metadata: {
            ...step.metadata,
            detectedPlatform: step.metadata?.detectedPlatform ?? "unknown",
            extractedFields: step.metadata?.extractedFields ?? {},
            comparedTransfers: step.metadata?.comparedTransfers ?? [],
          },
        }))
      },
      linkedTransfer: detailsData.linkedTransfer ? {...detailsData.linkedTransfer} : null,
      operation: detailsData.operation ? {...detailsData.operation, receipt: effectiveReceipt} : null
    }));

    // Manejo específico para REQUIRES_VALIDATION
    const currentStatus = transformedData.status || record.status;
    const previousStatus = currentStatusRef.current;
    currentStatusRef.current = currentStatus;

    if (currentStatus === "REQUIRES_VALIDATION" && previousStatus !== "REQUIRES_VALIDATION") {

      // Asegurarse de que haya un paso explícito de REQUIRES_VALIDATION
      if (transformedData.requestData?.processingSteps) {
        const steps = transformedData.requestData.processingSteps;

        // Verificar si ya existe un paso FINALIZATION o el último es REQUIRES_VALIDATION
        const hasFinalization = steps.some(s => s.step === "FINALIZATION" && s.status === "REQUIRES_VALIDATION");
        const lastStep = steps[steps.length - 1];
        const lastStepHasValidation = lastStep && lastStep.status === "REQUIRES_VALIDATION";

        if (!hasFinalization && !lastStepHasValidation) {
          transformedData.requestData.processingSteps.push({
            step: "FINALIZATION",
            timestamp: new Date().toISOString(),
            status: "REQUIRES_VALIDATION",
            details: "Se requiere validación manual",
            metadata: {
              processingStage: "VALIDATION",
              error: "Se requiere validación manual"
            }
          });
        }
      }

      // Stop polling when REQUIRES_VALIDATION is reached
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      // Forzar actualización inmediata del estado solo una vez
      setTimeout(() => {
        setUpdateCounter(prev => prev + 1);
        forceUpdate();
      }, 50);
    }

    // Log status changes
    if (currentStatus !== previousStatus) {
      if (currentStatus !== "REQUIRES_VALIDATION") {
        setUpdateCounter(prev => prev + 1);
      }
    }

    setDetails(transformedData);
    setError(null);
    setDataAttemptedLoadForId(record.id);

  } catch (error) {
    const axiosError = error as AxiosError;
    
    if (axiosError.response?.status === 401 || 
        axiosError.response?.status === 403 ||
        (axiosError.response?.data as any)?.code === 'NO_TOKEN') {
      setError("Error de autenticación. Por favor inicie sesión nuevamente.");
      localStorage.removeItem("token");
    } else {
      const errorMessage =
        error instanceof AxiosError 
          ? (error.response?.data?.error ?? error.message) 
          : "Error loading details";
      setError(errorMessage);
    }
    
    setDataAttemptedLoadForId(record.id);
  } finally {
    setLoading(false);
    setIsFetching(false);
  }
}, [record.id, record.status, record.type]); // Only essential dependencies


  // Add a function to start polling with shorter intervals for active requests
const startPolling = useCallback(() => {
  if (pollingIntervalRef.current) {
    clearInterval(pollingIntervalRef.current);
  }

  if (!record.id || !isActiveStatus(record.status)) {
    return;
  }


  pollingIntervalRef.current = setInterval(() => {
    // Check if status has become terminal - stop polling immediately
    if (currentStatusRef.current && isTerminalStatus(currentStatusRef.current)) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    // Only fetch if still in active processing state
    if (currentStatusRef.current && isActiveStatus(currentStatusRef.current)) {
      fetchDetails({ forceRefresh: true });
    }
  }, 2000);
}, [record.id, record.status]); // Removed fetchDetails, isFetching, isActiveStatus, isTerminalStatus from dependencies


  // Stop polling when component unmounts
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // Effect for handling record changes and starting polling
  useEffect(() => {
    let mounted = true;
    const token = getAndSanitize("token");

    if (!token) return;

    // Reset status when record changes
    if (record.id !== currentStatusRef.current) {
      currentStatusRef.current = record.status;
    }

    // Reset state for a new record
    if (dataAttemptedLoadForId !== record.id) {
      const hasValidDetailsForCurrentRecord = details &&
        details.requestId === record.id &&
        details.requestData?.processingSteps?.length > 0;

      if (!hasValidDetailsForCurrentRecord && !isFetching) {
        const loadInitialData = async () => {
          if (mounted) {
            await fetchDetails();

            // Start polling after initial data load if status is active
            if (isActiveStatus(record.status)) {
              startPolling();
            }
          }
        };

        loadInitialData();
      } else if (isActiveStatus(record.status) && !pollingIntervalRef.current) {
        // If we already have data but status is active and polling is not running, start it
        startPolling();
      }
    }

    return () => {
      mounted = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [record.id, dataAttemptedLoadForId, record.status]); // Removed fetchDetails, details, isFetching, startPolling from dependencies

  // Cargar imagen con autenticación para el modal
  const loadModalImage = useCallback(async (imagePath: string) => {
    if (!imagePath) return;
    
    setModalImageLoading(true);
    try {
      const token = getAndSanitize("token");
      if (!token) {
        console.error('[ProcessSteps] No token available for modal image');
        return;
      }
      
      // Normalize the image path
      let apiPath = imagePath;
      if (apiPath.startsWith('/uploads/')) {
        apiPath = apiPath.substring(9);
      } else if (apiPath.startsWith('uploads/')) {
        apiPath = apiPath.substring(8);
      }
      
      // Just use the direct URL to the image instead of creating a blob
      const imageUrl = `${API_BASE_URL}/api/panel/uploads/${apiPath}`;
      setImageSrc(imageUrl);
    } catch (error) {
      console.error('[ProcessSteps] Error processing image path:', error);
    } finally {
      setModalImageLoading(false);
    }
  }, []);
    
  // Limpiar URL del blob al cerrar el modal
  const closeImageModal = useCallback((): void => {
    setImageSrc(null);
    setImageModal({
      visible: false,
      image: null
    });
  }, []);
    
  // Cargar la imagen cuando se abre el modal
  const handleImageClick = useCallback((imagePath: string): void => {
    setImageModal({
      visible: true,
      image: imagePath
    });
    loadModalImage(imagePath);
  }, [loadModalImage]);

  const handleContentLoad = useCallback((section: keyof ContentLoadingState): void => {
    setContentLoading(prev => ({
      ...prev,
      [section]: false
    }));
  }, []);

const memoizedOperationSummary = useMemo(() => {
  if (record.type !== "LOAD" || !details) return null;
  
  // Asegurarse de que todos los datos tengan nuevas referencias
  const recordCopy = {...record, status: details.status || record.status, id: record.id};
  const detailsCopy = JSON.parse(JSON.stringify(details));
  
  return (
    <Suspense fallback={<Skeleton active />}>
      <OperationSummary 
        record={recordCopy}
        details={detailsCopy}
        loading={contentLoading.summary}
        onLoad={() => handleContentLoad('summary')}
        key={`summary-${record.id}-${updateCounter}-${details.status || 'unknown'}`}
      />
    </Suspense>
  );
}, [record.id, record.type, details?.status, updateCounter, contentLoading.summary]); // Dependencias mínimas



  const memoizedWithdrawDetails = useMemo(() => {
    if (record.type !== "WITHDRAW" || !details) return null;

    // Ensure all receipt paths are properly passed
    const enrichedRecord = {
      ...record,
      status: details.status || record.status, // Use latest status
      details: {
        ...details,
        receipt: details.receipt || record.receipt || details.operation?.receipt,
        status: details.status || record.status // Use latest status
      },
      receipt: details.receipt || record.receipt || details.operation?.receipt,
      operation: {
        ...details.operation,
        receipt: details.receipt || record.receipt || details.operation?.receipt,
        withdraw: {
          ...details.operation?.withdraw,
          receipt: details.receipt || record.receipt || details.operation?.receipt
        }
      }
    };

    return (
      <Suspense fallback={<Skeleton active />}>
        <WithdrawDetails 
          record={enrichedRecord}
          onImageClick={handleImageClick}
          loading={contentLoading.details}
          onLoad={() => handleContentLoad('details')}
          key={`withdraw-${record.id}-${updateCounter}`} // Force re-render with key
        />
      </Suspense>
    );
  }, [record, details, handleImageClick, contentLoading.details, handleContentLoad, updateCounter]);

const memoizedValidationContent = useMemo(() => {
  if (record.type === "WITHDRAW" || !details) return null;

  // Asegurarse de que todos los datos tengan nuevas referencias
  const recordCopy = {...record, status: details.status || record.status, id: record.id};
  const detailsCopy = JSON.parse(JSON.stringify({
    ...details,
    status: details.status || record.status,
    requestData: {
      retryHistory: details.requestData?.retryHistory || [],
      processingSteps: details.requestData?.processingSteps || [],
    }
  }));

  return (
    <ValidationContent
      details={detailsCopy}
      record={recordCopy}
      loading={loading}
      onLoad={() => handleContentLoad('validation')}
      key={`validation-${record.id}-${updateCounter}-${details.status || 'unknown'}`}
    />
  );
}, [record.id, record.type, details?.status, updateCounter, loading]); // Dependencias mínimas


  // Calculate responsive modal width based on screen size
  const getModalWidth = () => {
    const screenWidth = window.innerWidth;
    if (screenWidth <= 576) {
      return '95%'; // Mobile
    } else if (screenWidth <= 768) {
      return '85%'; // Tablet
    } else {
      return 800; // Desktop
    }
  };

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" danger onClick={() => fetchDetails()}>
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <div id={`process-steps-${record.id}`} className="process-steps-container">
      <Card variant="borderless" className="process-card" styles={{
  body: { 
    padding: 0 
  }
}}>

        {record.type === "WITHDRAW" ? (
          // For withdrawals, render without the column layout
          <div className="withdraw-container">
            {memoizedWithdrawDetails}
          </div>
        ) : (
          // For other types, keep the original layout with mobile optimizations
          <Row gutter={[0, 16]} className="process-row">
            <Col xs={24} lg={24} className="summary-col">
              {memoizedOperationSummary}
            </Col>
            <Col xs={24} md={24} lg={12} className="receipt-column">
              <Suspense fallback={<Skeleton active />}>
                <ReceiptCard
                  loading={loading}
                  record={{...record, status: details?.status || record.status}}
                  details={details}
                  onImageClick={handleImageClick}
                  key={`receipt-${record.id}-${updateCounter}`} // Force re-render with key
                />
              </Suspense>
            </Col>
            <Col xs={24} md={24} lg={12} className="validation-column">
              {memoizedValidationContent}
            </Col>
          </Row>
        )}
      </Card>

      <Modal 
        open={imageModal.visible} 
        footer={null} 
        onCancel={closeImageModal} 
        width={getModalWidth()}
        centered
        destroyOnClose
        className="image-preview-modal"
styles={{
  body: { 
    padding: 0 
  }
}}
      >
        {modalImageLoading ? (
          <div className="modal-loading-container">
            <Skeleton.Image active style={{ width: '100%', height: 300 }} />
          </div>
        ) : imageSrc ? (
          <div className="image-container">
            <img
              src={imageSrc}
              className="modal-image"
              alt="Receipt"
            />
          </div>
        ) : (
          <div className="modal-error-container">
            <Alert
              message="Error al cargar la imagen"
              description="No se ha podido cargar la imagen con autenticación"
              type="error"
              showIcon
            />
          </div>
        )}
      </Modal>

      <style>{`
  .process-steps-container {
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
    padding: 0; /* Asegurarnos que no hay padding aquí */
  }
  
  .process-card {
    width: 100%;
    margin-bottom: 16px;
    overflow: hidden;
    /* Eliminar cualquier padding que pudiera estar aquí */
  }
  
  .process-card .ant-card-body {
    padding: 0 !important; /* Forzar override del padding de Ant Design */
  }
  
  .process-row {
    width: 100%;
    margin: 0 !important; /* Eliminar los márgenes del Row */
  }

  /* También asegurar que las columnas no tengan padding lateral */
  .summary-col, .receipt-column, .validation-column {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
        
        .withdraw-container {
          width: 100%;
        }
        
        .receipt-column, .validation-column {
          margin-bottom: 16px;
        }
        
        .step-card {
          height: 100%;
          transition: all 0.3s;
        }
        
        .receipt-container {
          width: 100%;
          height: auto;
          max-height: 600px;
          min-height: 300px;
          overflow: hidden;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
          position: relative;
        }
        
        .receipt-container img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
        }
        
        .receipt-container:hover {
          opacity: 0.8;
        }
        
        .summary-bar {
          background: #fafafa;
          margin-top: 16px;
          padding: 12px;
          border-radius: 4px;
        }
        
        .summary-bar.completed {
          border-left: 4px solid #52c41a;
        }
        
        .summary-bar.rejected {
          border-left: 4px solid #ff4d4f;
        }
        
        .summary-bar.pending {
          border-left: 4px solid #1890ff;
        }
        
        .modal-loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 300px;
          width: 100%;

        }
        
        .image-container {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 100%;
          max-height: 90vh;
          background-color: rgba(0, 0, 0, 0.03);
          overflow: hidden;
        }
        
        .modal-image {
          width: 100%;
          height: auto;
          max-height: 90vh;
          object-fit: contain;
          touch-action: pan-y pinch-zoom;
        }
        
        .modal-error-container {
          padding: 24px;
          text-align: center;
        }
        
        .image-preview-modal .ant-modal-content {
          overflow: hidden;
          border-radius: 8px;
        }
        
        /* Mobile optimizations */
        @media (max-width: 768px) {
          .process-card {

          }
          
          .receipt-container {
            min-height: 200px;
          }
          
          .summary-bar {
            padding: 8px;
            margin-top: 8px;
          }
          
          .modal-image {
            max-width: 100%;
          }
        }
      `}</style>
    </div>
  );
}, (prevProps, nextProps) => {
  // Get processing steps from both records  

  // Return true only if nothing important changed (prevents unnecessary re-renders)
  return (
    prevProps.record.id === nextProps.record.id &&
    prevProps.record.status === nextProps.record.status &&
    prevProps.record.linkedTransfer?.id === nextProps.record.linkedTransfer?.id
  );
});

ProcessSteps.displayName = "ProcessSteps";

export default ProcessSteps;